import { GitHubLinksAnnotationBadge, IGitHubLinksAnnotationBadgeOptions } from "Agile/Scripts/Card/Annotations/GitHubLinksAnnotation/Badge";
import {
    createOrUpdateGitHubLinksDetailsList,
    disposeGitHubLinksDetailsList
} from "Agile/Scripts/Card/Annotations/GitHubLinksAnnotation/DetailsList";
import { GitHubLinkDataHelper } from "Agile/Scripts/Card/Annotations/GitHubLinksAnnotation/GitHubLinkDataHelper";
import { GitHubLinksAnnotationItemSource } from "Agile/Scripts/Card/Annotations/GitHubLinksAnnotation/ItemSource";
import { AnnotationAdapter, AnnotationDetailPaneState, IAnnotationAdapterOptions } from "Agile/Scripts/Card/CardsAnnotationsCommon";
import { Board, KanbanTelemetry } from "Agile/Scripts/TFS.Agile.Boards";
import { ILinkedArtifact } from "TFS/WorkItemTracking/ExtensionContracts";
import { Control } from "VSS/Controls";
import "VSS/LoaderPlugins/Css!Agile/Card/Annotations/GitHubLinksAnnotation/Styles";

export interface IGitHubLinksAnnotationAdapterOptions extends IAnnotationAdapterOptions {}

export class GitHubLinksAnnotationAdapter extends AnnotationAdapter {
    private _isApplicable: boolean;
    private _githubLinks: ILinkedArtifact[];
    private _githubLinkDataHelper: GitHubLinkDataHelper;

    constructor(options: IGitHubLinksAnnotationAdapterOptions) {
        super({
            ...options,
            id: options.annotationId,
            priority: 30,
            $annotationDetailPaneContainer: $("<div/>").addClass("github-links-details-pane"),
            $badgeContainer: $("<div/>").addClass("badge github-links-annotation")
        });

        this._githubLinkDataHelper = new GitHubLinkDataHelper();
        this._isApplicable = Board.BoardAnnotationSettings.isAnnotationApplicable(this.options.id);
    }

    /**
     * Disposes the elements associated with checklist adapter
     */
    public dispose() {
        // We need to unmount the React control before super.dispose clears the container.
        this._removeGitHubLinksList(true);

        super.dispose();

        if (this.options.$annotationDetailPaneContainer.children()) {
            this.options.$annotationDetailPaneContainer.children().detach();
        }

        if (this.options.$badgeContainer.children()) {
            this.options.$badgeContainer.children().detach();
        }
    }

    /**
     * Refreshes the badge control. It creates a new control if not present already
     * otherwise updates/deletes it as required.
     */
    public refreshAnnotation() {
        super.refreshAnnotation();

        if (!this._isApplicable || !this.options.source || this.options.source.id() <= 0) {
            // No op if annotation no applicable or the work item source not available
            return;
        }

        const annotationItemSource = this.options.source.getAnnotationItemSource(GitHubLinksAnnotationItemSource.sourceType);
        this._githubLinks = annotationItemSource.getItem(this.options.source.id());
        if (this._githubLinks && this._githubLinks.length > 0) {
            // Create the annotation if not created already, otherwise just update it
            if (this.options.$badgeContainer.children().length === 0) {
                this._ensureAnnotation();
            } else {
                this._updateAnnotation();
            }
        } else {
            this.dispose();
        }
    }

    private _ensureAnnotation(): void {
        if (!this.badgeControl) {
            this._createAnnotation();
        }
        this.badgeControl.update(this._githubLinks);
    }

    private _createAnnotation(): void {
        this.badgeControl = Control.create(GitHubLinksAnnotationBadge, this.options.$badgeContainer, {
            clickEventHandler: (e) => {
                if (!this.$annotationDetailPaneControl) {
                    // Log telemetry that user intend to open details pane
                    KanbanTelemetry.publish("GitHubLinksAnnotation.Open", {
                        WorkItemType: this.options.source.type(),
                        LinksCount: this._githubLinks.length
                    });

                    this._createGitHubLinksList();
                } else {
                    this._removeGitHubLinksList();
                }
                e.preventDefault();
            }
        } as IGitHubLinksAnnotationBadgeOptions);
        this.options.$badgeContainer.append(this.badgeControl.getElement()[0]);
    }

    private _createGitHubLinksList(): void {
        // Create details control
        this.$annotationDetailPaneControl = $("<div/>")
            .addClass(AnnotationAdapter.TILE_CONTENT_DETAILS)
            .appendTo(this.options.$annotationDetailPaneContainer);
        createOrUpdateGitHubLinksDetailsList(
            this.$annotationDetailPaneControl[0],
            this.options.source.id(),
            this._githubLinks,
            this._githubLinkDataHelper
        );

        // Show the details
        this._openDetailsPane();
    }

    private _removeGitHubLinksList(resetFocus?: boolean): void {
        if (this.$annotationDetailPaneControl && this.$annotationDetailPaneControl.length > 0) {
            // Dispose controls
            disposeGitHubLinksDetailsList(this.$annotationDetailPaneControl[0]);
            this.$annotationDetailPaneControl = null;

            // Clear the container
            if (this.options.$annotationDetailPaneContainer.children()) {
                this.options.$annotationDetailPaneContainer.children().detach();
            }

            this._closeDetailsPane();

            if (resetFocus) {
                this.setFocusToTile();
            }
        }
    }
    private _updateAnnotation(): void {
        this._ensureAnnotation();

        if (this.$annotationDetailPaneControl && this.$annotationDetailPaneControl.length > 0) {
            // Update details control
            createOrUpdateGitHubLinksDetailsList(
                this.$annotationDetailPaneControl[0],
                this.options.source.id(),
                this._githubLinks,
                this._githubLinkDataHelper
            );
        }
    }

    private _openDetailsPane(): void {
        this.options.onAnnotationDetailPaneStateChange(this, AnnotationDetailPaneState.OPENED);
    }

    private _closeDetailsPane(): void {
        this.options.onAnnotationDetailPaneStateChange(this, AnnotationDetailPaneState.CLOSED);
    }
}
